import stoneTheme from '@stone-payments/jade-design-tokens/cjs/stone'
import tonTheme from '@stone-payments/jade-design-tokens/cjs/ton'
import { TIME_TO_EXPIRE_TOKEN } from 'shared/envs'
import { isomorphicCookies } from 'shared/storage'

import { isNativeBridgeRejectEvent, sendMessage } from '../webview-sdk/native-bridge'
import { NativeMessageType } from '../webview-sdk/native-message-type'

import { parseToken } from './decode-jwt-payload'
import { detectClientIsWebView, isAndroidWebView, isIosWebView } from './webview-functions'
import { exposeToWindow } from './window-functions'

import '@stone-payments/jade/css/index.css'

export interface WebviewUserData {
  identityId: string
  accountId: string
}

export interface MobileTokens {
  refreshToken: string
  token: string
}

export interface WebviewRuntime {
  isWebView: () => boolean
  webviewGetTokens: (params: MobileTokens) => { token?: string; refreshToken?: string }
  webviewGetUserData: (params: WebviewUserData) => { identityId?: string; accountId?: string }
  webviewFinishUrl: () => string
  webviewFinishMethod: () => void
  webviewExternalNavigation: (params: { url: string }) => void
  webviewErrorHandler: (error: Error) => void
  webviewErrorUrl?: string
  webviewGetAppInfo: (params: { appPlatform: 'ton' | 'stone' }) => void
  registerFinishInterceptor: (interceptor: () => Promise<void> | void) => void
  unregisterFinishInterceptor: () => void
}

export function sanitizeTokens(token: string): string {
  const CONTROL_CHAR_MAX = 0x1f
  const DELETE_CHAR = 0x7f
  const DISALLOWED_CHARS = ['"', "'", ';', '\\', '/', ' ']

  if (typeof token !== 'string') return ''

  return token
    .split('')
    .filter(char => {
      const code = char.charCodeAt(0)
      return code > CONTROL_CHAR_MAX && code !== DELETE_CHAR && !DISALLOWED_CHARS.includes(char)
    })
    .join('')
}
export const WEBVIEW_FINISH_URL = 'app://webviewFinish'
export const WEBVIEW_ERROR_URL = 'app://webviewError'
export const WEBVIEW_LOAD_USER_URL = 'app://loadUser'

let finishInterceptor: (() => Promise<void> | void) | null = null
let tokenMonitorIntervalId: ReturnType<typeof setInterval> | null = null

let pendingTokenResolve: ((tokens: MobileTokens) => void) | null = null
let pendingTokenPromise: Promise<MobileTokens> | null = null
const TOKEN_DELIVERY_TIMEOUT_MS = 10_000

const REFRESH_BUFFER_MS = 60 * 1000
const MIN_REFRESH_INTERVAL_MS = 10 * 1000
const TOKEN_CHECK_INTERVAL_MS = 15 * 1000
let lastRefreshTimestamp = 0
let lastRefreshedTokenExp: number | null = null
let tokenExpiredListener: (() => void) | null = null

/**
 * Decodifica um JWT e retorna o campo `exp` em milissegundos (Unix timestamp).
 * Retorna null se o token for inválido ou não tiver `exp`.
 */
export function decodeTokenExpiration(token: string): number | null {
  const payload = parseToken({ token })
  if (typeof payload.exp !== 'number') return null
  return payload.exp * 1000
}

function requestTokenRefresh(expMs: number): boolean {
  if (lastRefreshedTokenExp === expMs) return false

  const now = Date.now()
  if (now - lastRefreshTimestamp < MIN_REFRESH_INTERVAL_MS) return false

  lastRefreshTimestamp = now
  lastRefreshedTokenExp = expMs

  if (isPlaygroundMode()) {
    console.info(
      '[WebviewTokenRefresh] Playground: em webview real, o app nativo enviaria tokens frescos via app://loadUser'
    )
    return true
  }

  console.info('[WebviewTokenRefresh] Requesting fresh tokens via redirectToLoadUser()')
  redirectToLoadUser()
  return true
}

/**
 * Inicia monitoramento contínuo do token JWT.
 *
 * A cada 15 segundos, lê o token dos cookies, decodifica o `exp`, e verifica
 * se falta menos de 60s para expirar. Se sim, chama `redirectToLoadUser()`
 * para que o app nativo envie tokens novos antes da expiração.
 *
 * - Idempotente: chamar múltiplas vezes não cria intervalos duplicados
 * - Auto-sustentável: continua monitorando mesmo após refresh
 * - Debounce de 10s entre refreshes para evitar floods
 * - Não retenta se o token não mudou desde a última tentativa de refresh
 * - Chamada automaticamente por `webviewGetTokens()` quando tokens chegam
 *
 * @returns info sobre o estado atual do token, ou null se não há token
 */
export function scheduleTokenRefresh(): {
  expiresAt: Date
  timeUntilExpiryMs: number
  isMonitoring: boolean
} | null {
  if (typeof window === 'undefined') return null

  const { token } = isomorphicCookies.getAll()
  if (!token) {
    console.warn('[WebviewTokenRefresh] No token found in cookies')
    return null
  }

  const expiresAtMs = decodeTokenExpiration(token)
  if (expiresAtMs === null) {
    console.warn('[WebviewTokenRefresh] Could not decode token expiration')
    return null
  }

  if (lastRefreshedTokenExp !== null && lastRefreshedTokenExp !== expiresAtMs) {
    lastRefreshedTokenExp = null
  }

  const now = Date.now()
  const timeUntilExpiry = expiresAtMs - now

  if (timeUntilExpiry <= 0) {
    console.info(`[WebviewTokenRefresh] Token already expired ${Math.round(-timeUntilExpiry / 1000)}s ago`)
    requestTokenRefresh(expiresAtMs)
  } else if (timeUntilExpiry <= REFRESH_BUFFER_MS) {
    console.info(`[WebviewTokenRefresh] Token expiring soon (${Math.round(timeUntilExpiry / 1000)}s left) — refreshing`)
    requestTokenRefresh(expiresAtMs)
  } else {
    console.info(
      `[WebviewTokenRefresh] Token valid for ${Math.round(timeUntilExpiry / 1000)}s, ` +
        `refresh in ${Math.round((timeUntilExpiry - REFRESH_BUFFER_MS) / 1000)}s`
    )
  }

  if (tokenMonitorIntervalId === null) {
    tokenMonitorIntervalId = setInterval(() => {
      const cookies = isomorphicCookies.getAll()
      if (!cookies.token) return

      const expMs = decodeTokenExpiration(cookies.token)
      if (expMs === null) return

      const remaining = expMs - Date.now()

      if (remaining <= 0) {
        const refreshed = requestTokenRefresh(expMs)
        if (!refreshed) {
          console.error('[WebviewTokenRefresh] Token expired and refresh failed — blocking session')
          cancelTokenRefresh()
          tokenExpiredListener?.()
          webviewRuntime.webviewErrorHandler(new Error('Session expired'))
        }
        return
      }

      if (remaining <= REFRESH_BUFFER_MS) {
        requestTokenRefresh(expMs)
      }
    }, TOKEN_CHECK_INTERVAL_MS)

    console.info('[WebviewTokenRefresh] Token monitor started (checking every 15s)')
  }

  return {
    expiresAt: new Date(expiresAtMs),
    timeUntilExpiryMs: timeUntilExpiry,
    isMonitoring: true
  }
}

/**
 * Solicita tokens frescos ao app nativo e retorna uma Promise que resolve
 * quando `webviewGetTokens()` for chamado com os novos tokens.
 *
 * - Em webview real: navega para `app://loadUser`, app nativo chama `webviewGetTokens()`
 * - Em playground: recicla tokens existentes via `redirectToLoadUser()`
 * - Timeout de 10s caso o app nativo não responda
 * - Deduplicação: se já há um pedido pendente, retorna a mesma Promise
 *
 * Usado pelo interceptor de 401 para retry automático de requisições.
 */
export function requestFreshTokens(): Promise<MobileTokens> {
  if (pendingTokenPromise) return pendingTokenPromise

  const promise = new Promise<MobileTokens>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      console.warn('[WebviewTokenRefresh] Token delivery timeout — native app did not respond in 10s')
      pendingTokenResolve = null
      pendingTokenPromise = null
      reject(new Error('Token delivery timeout'))
    }, TOKEN_DELIVERY_TIMEOUT_MS)

    pendingTokenResolve = (tokens: MobileTokens) => {
      clearTimeout(timeoutId)
      pendingTokenResolve = null
      pendingTokenPromise = null
      resolve(tokens)
    }
  })

  pendingTokenPromise = promise
  redirectToLoadUser()

  return pendingTokenPromise
}

export function onTokenExpired(listener: () => void): void {
  tokenExpiredListener = listener
}

export function offTokenExpired(): void {
  tokenExpiredListener = null
}

/**
 * Para o monitoramento contínuo de expiração do token.
 */
export function cancelTokenRefresh(): void {
  if (tokenMonitorIntervalId !== null) {
    clearInterval(tokenMonitorIntervalId)
    tokenMonitorIntervalId = null
    lastRefreshedTokenExp = null
    console.info('[WebviewTokenRefresh] Token monitor stopped')
  }
}

const PLAYGROUND_STORAGE_KEY = '__playground_mode'

export function isPlaygroundMode(): boolean {
  if (typeof window === 'undefined') return false

  const fromQuery = new URLSearchParams(window.location.search).get('playground') === 'true'
  if (fromQuery) {
    try {
      sessionStorage.setItem(PLAYGROUND_STORAGE_KEY, 'true')
    } catch {
      // sessionStorage not available
    }
    return true
  }

  try {
    return sessionStorage.getItem(PLAYGROUND_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function notifyPlayground(type: 'info' | 'warn' | 'error' | 'bridge', message: string): void {
  if (!isPlaygroundMode()) return
  try {
    const target = window.parent !== window ? window.parent : window
    target.postMessage({ __playgroundLog: true, type, message }, '*')
  } catch {
    // cross-origin or unavailable, silently ignore
  }
}

function getInterceptInfo(url: string): {
  icon: string
  title: string
  description: string
  detail: string
  accentColor: string
  bgColor: string
} {
  if (url.includes('webviewFinish')) {
    return {
      icon: '\u2713',
      title: 'Webview finalizada',
      description:
        'A pagina chamou webviewFinishMethod(). ' +
        'No app real, isso fecha a webview e retorna o usuario para a tela nativa anterior.',
      detail: 'Protocolo: app://webviewFinish',
      accentColor: '#00a868',
      bgColor: '#f0fdf4'
    }
  }

  if (url.includes('webviewError')) {
    const errorMsg = new URL(url, 'http://localhost').searchParams.get('message') || 'erro desconhecido'
    return {
      icon: '!',
      title: 'Erro na webview',
      description:
        'A pagina disparou webviewErrorHandler(). ' +
        'No app real, isso fecha a webview e mostra uma tela de erro nativa ao usuario.',
      detail: `Mensagem: "${errorMsg}"`,
      accentColor: '#dc2626',
      bgColor: '#fef2f2'
    }
  }

  if (url.startsWith('external://')) {
    const externalUrl = url.replace('external://', '')
    return {
      icon: '\u2197',
      title: 'Link externo',
      description:
        'A pagina abriu um link externo via webviewExternalNavigation(). ' +
        'No app real, isso abriria o navegador do celular (Chrome/Safari) com essa URL.',
      detail: `URL: ${externalUrl}`,
      accentColor: '#0284c7',
      bgColor: '#f0f9ff'
    }
  }

  if (url.startsWith('app://')) {
    const command = url.replace('app://', '').split('?')[0]
    const params = url.includes('?') ? url.split('?')[1] : null
    return {
      icon: '\u2699',
      title: `Comando nativo: ${command}`,
      description:
        `A pagina enviou o comando "${command}" via protocolo app://. ` +
        'No app real, o app nativo receberia esse comando e executaria a acao correspondente.',
      detail: params ? `Parametros: ${decodeURIComponent(params)}` : 'Sem parametros',
      accentColor: '#7c3aed',
      bgColor: '#f5f3ff'
    }
  }

  // Regular page navigation
  const pathname = url.split('?')[0]
  return {
    icon: '\u2192',
    title: 'Navegacao interceptada',
    description:
      'A pagina tentou navegar para outra URL via window.location. ' +
      'No app real, a webview carregaria essa nova pagina dentro do container nativo.',
    detail: `Destino: ${decodeURIComponent(pathname)}`,
    accentColor: '#1a1a1a',
    bgColor: '#f5f5f5'
  }
}

function renderPlaygroundInterceptScreen(url: string): void {
  const doc = globalThis.document
  if (!doc) return

  const existing = doc.getElementById('__playground-intercept')
  if (existing) existing.remove()

  const info = getInterceptInfo(url)

  const overlay = doc.createElement('div')
  overlay.id = '__playground-intercept'
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '99999',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.5)',
    padding: '32px',
    textAlign: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
  })

  const card = doc.createElement('div')
  Object.assign(card.style, {
    background: '#fff',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '380px',
    width: '100%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
  })

  // Badge: "MODO PLAYGROUND"
  const badge = doc.createElement('div')
  Object.assign(badge.style, {
    fontSize: '10px',
    fontWeight: '700',
    color: '#fff',
    background: '#6366f1',
    borderRadius: '4px',
    padding: '3px 8px',
    display: 'inline-block',
    letterSpacing: '1px',
    marginBottom: '16px'
  })
  badge.textContent = 'MODO PLAYGROUND'

  const icon = doc.createElement('div')
  Object.assign(icon.style, {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: info.bgColor,
    color: info.accentColor,
    fontSize: '24px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
    border: `2px solid ${info.accentColor}`
  })
  icon.textContent = info.icon

  const title = doc.createElement('div')
  Object.assign(title.style, {
    fontSize: '16px',
    fontWeight: '700',
    color: info.accentColor,
    marginBottom: '8px'
  })
  title.textContent = info.title

  const desc = doc.createElement('div')
  Object.assign(desc.style, {
    fontSize: '13px',
    color: '#555',
    lineHeight: '1.5',
    marginBottom: '12px'
  })
  desc.textContent = info.description

  const detailBox = doc.createElement('div')
  Object.assign(detailBox.style, {
    fontSize: '12px',
    fontFamily: 'monospace',
    color: '#666',
    background: info.bgColor,
    borderRadius: '8px',
    padding: '8px 12px',
    wordBreak: 'break-all',
    marginBottom: '20px'
  })
  detailBox.textContent = info.detail

  const hint = doc.createElement('div')
  Object.assign(hint.style, {
    fontSize: '11px',
    color: '#aaa',
    marginBottom: '16px',
    lineHeight: '1.4'
  })
  hint.textContent =
    'Esta acao foi interceptada porque voce esta no modo playground. ' +
    'No app real (Stone/Ton), ela seria executada normalmente pelo app nativo.'

  // Dismiss button
  const backBtn = doc.createElement('button')
  Object.assign(backBtn.style, {
    width: '100%',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#fff',
    background: info.accentColor,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer'
  })
  backBtn.textContent = '\u2190 Voltar a pagina'
  backBtn.onclick = () => overlay.remove()

  card.append(badge, icon, title, desc, detailBox, hint, backBtn)
  overlay.appendChild(card)

  // Also dismiss on clicking the backdrop (outside the card)
  overlay.onclick = (e: MouseEvent) => {
    if (e.target === overlay) overlay.remove()
  }

  doc.body.appendChild(overlay)
}

export function webviewLocationReplace(url: string): void {
  if (isPlaygroundMode()) {
    if (url.startsWith('app://')) {
      console.info('[WebviewRuntime] Playground: intercepted native command (replace)', url)
      notifyPlayground('info', `Comando nativo interceptado: ${url}`)
      if (!url.includes(WEBVIEW_LOAD_USER_URL)) {
        renderPlaygroundInterceptScreen(url)
      }
      return
    }
    // Use History API to update URL without full page reload (avoids white screen flash in iframe)
    console.info('[WebviewRuntime] Playground: simulating navigation (replace) to', url)
    notifyPlayground('info', `Navegacao simulada (replace): ${url}`)
    try {
      history.replaceState(null, '', url)
    } catch {
      window.location.replace(url)
    }
    return
  }
  window.location.replace(url)
}

export function webviewLocationAssign(url: string): void {
  if (isPlaygroundMode()) {
    if (url.startsWith('app://')) {
      console.info('[WebviewRuntime] Playground: intercepted native command (assign)', url)
      notifyPlayground('info', `Comando nativo interceptado: ${url}`)
      renderPlaygroundInterceptScreen(url)
      return
    }
    // Use History API to update URL without full page reload (avoids white screen flash in iframe)
    console.info('[WebviewRuntime] Playground: simulating navigation to', url)
    notifyPlayground('info', `Navegacao simulada (push): ${url}`)
    try {
      history.pushState(null, '', url)
    } catch {
      window.location.href = url
    }
    return
  }
  window.location.href = url
}

/**
 * Retorna a plataforma do app (stone ou ton) baseada no cookie appPlatform
 * @returns 'stone' | 'ton'
 */
export function getAppPlatform(): 'stone' | 'ton' {
  if (typeof window === 'undefined') return 'stone'
  const { appPlatform } = isomorphicCookies.getAll()
  return appPlatform === 'ton' ? 'ton' : 'stone'
}

/**
 * Retorna o tema correto (Stone ou Ton) baseado na plataforma do app
 * @returns Tema do Jade Design Tokens (stone ou ton)
 */
export function getJadeTheme() {
  const platform = getAppPlatform()
  return platform === 'ton' ? tonTheme : stoneTheme
}

/**
 * Hook para usar o tema correto baseado na plataforma
 * @param appTheme Tema opcional que pode ser passado explicitamente
 * @returns Tema do Jade Design Tokens
 */
export function useJadeTheme(appTheme?: 'stone-rebrand' | 'stone' | 'ton') {
  const platform = appTheme ?? getAppPlatform()
  return platform === 'ton' ? tonTheme : stoneTheme
}

const baseRuntime: WebviewRuntime = {
  isWebView: () => {
    const isClient = typeof window !== 'undefined'
    const detected = detectClientIsWebView()

    if (isClient && detected) {
      isomorphicCookies.set('isWebView', 'true', { maxAge: 60 * 60 * 24 * 30 * 6, path: '/' })
      console.info('[WebviewRuntime] isWebView (short-circuit by detectClientIsWebView): true')
      return true
    }

    const result = isClient && (isAndroidWebView() || isIosWebView())

    if (result) {
      isomorphicCookies.set('isWebView', 'true', { maxAge: 60 * 60 * 24 * 30 * 6, path: '/' })
    }

    return result
  },

  webviewGetTokens: tokens => {
    console.info('[webviewRuntime] - called webviewGetTokens')
    isomorphicCookies.set('isWebView', 'true', { maxAge: 60 * 60 * 24 * 30 * 6, path: '/' })
    isomorphicCookies.set('refreshToken', tokens.refreshToken, { path: '/', maxAge: TIME_TO_EXPIRE_TOKEN })
    isomorphicCookies.set('token', tokens.token, { path: '/', maxAge: TIME_TO_EXPIRE_TOKEN })
    const { token, refreshToken } = isomorphicCookies.getAll()

    scheduleTokenRefresh()

    if (pendingTokenResolve) {
      console.info('[WebviewRuntime] Resolving pending token request with fresh tokens')
      pendingTokenResolve({ token: token!, refreshToken: refreshToken! })
    }

    return {
      token,
      refreshToken
    }
  },

  webviewGetUserData: data => {
    console.info('[webviewRuntime] - called webviewGetUserData')
    isomorphicCookies.set('isWebView', 'true', { maxAge: 60 * 60 * 24 * 30 * 6, path: '/' }) // 180 dias
    isomorphicCookies.set('identityId', data.identityId, { maxAge: 60 * 60 * 24 * 30 * 6, path: '/' }) // 180 dias
    isomorphicCookies.set('accountId', data.accountId, { maxAge: 60 * 60 * 24 * 30 * 6, path: '/' }) // 180 dias
    const { identityId, accountId } = isomorphicCookies.getAll()
    return {
      identityId,
      accountId
    }
  },

  webviewFinishUrl: () => {
    return WEBVIEW_FINISH_URL
  },

  webviewFinishMethod: async () => {
    console.info('[WebviewRuntime] WebView finish triggered')

    if (finishInterceptor) {
      console.info('[WebviewRuntime] Executing finish interceptor before cleanup')
      try {
        await finishInterceptor()
        console.info('[WebviewRuntime] Finish interceptor completed successfully')
      } catch (error) {
        console.error('[WebviewRuntime] Finish interceptor failed:', error)
      }
    }

    cancelTokenRefresh()
    finishInterceptor = null

    if (isPlaygroundMode()) {
      console.info('[WebviewRuntime] Playground: intercepted webviewFinishMethod (skipping cleanup)')
      notifyPlayground('info', 'Simulacao de app nativo: webview finalizada (redirect para app://webviewFinish)')
      try {
        const target = window.parent !== window ? window.parent : window
        target.postMessage({ __playgroundFinish: true }, '*')
      } catch {
        // cross-origin or unavailable
      }
      renderPlaygroundInterceptScreen(WEBVIEW_FINISH_URL)
      return
    }

    console.info('[WebviewRuntime] Clearing cookies and redirecting to app://webviewFinish')
    isomorphicCookies.batchRemove([
      'token',
      'refreshToken',
      'identityId',
      'accountId',
      'idAssessment',
      'isWebView',
      'appPlatform'
    ])
    localStorage.clear()
    sessionStorage.clear()
    webviewLocationAssign(WEBVIEW_FINISH_URL)
  },

  webviewExternalNavigation: ({ url }) => {
    if (!url || typeof url !== 'string') return
    console.info('[WebviewRuntime] External navigation to:', url)
    if (isPlaygroundMode()) {
      notifyPlayground('info', `Simulacao de app nativo: abriu link externo ${url}`)
      renderPlaygroundInterceptScreen(`external://${url}`)
      return
    }
    window.open(url, '_blank')
  },

  webviewErrorHandler: error => {
    console.error('[WebviewRuntime] Unhandled WebView error:', error)

    cancelTokenRefresh()
    finishInterceptor = null

    const errorUrl = `${WEBVIEW_ERROR_URL}?message=${encodeURIComponent(error.message)}`

    if (isPlaygroundMode()) {
      console.info('[WebviewRuntime] Playground: intercepted webviewErrorHandler (skipping cleanup)')
      notifyPlayground('info', `Simulacao de app nativo: erro reportado (redirect para ${errorUrl})`)
      renderPlaygroundInterceptScreen(errorUrl)
      sendMessage(NativeMessageType.ERROR, { message: error.message }).catch(bridgeError =>
        console.error(
          `[WebviewRuntime] Native Bridge integration error ${
            isNativeBridgeRejectEvent(bridgeError) ? bridgeError : JSON.stringify(bridgeError)
          }`
        )
      )
      return
    }

    isomorphicCookies.batchRemove([
      'token',
      'refreshToken',
      'identityId',
      'accountId',
      'idAssessment',
      'isWebView',
      'appPlatform'
    ])
    localStorage.clear()
    sessionStorage.clear()

    webviewLocationReplace(errorUrl)

    sendMessage(NativeMessageType.ERROR, { message: error.message }).catch(bridgeError =>
      console.error(
        `[WebviewRuntime] Native Bridge integration error ${
          isNativeBridgeRejectEvent(bridgeError) ? bridgeError : JSON.stringify(bridgeError)
        }`
      )
    )
  },
  webviewErrorUrl: WEBVIEW_ERROR_URL,

  webviewGetAppInfo: ({ appPlatform }: { appPlatform: 'ton' | 'stone' }) => {
    isomorphicCookies.set('appPlatform', appPlatform, { maxAge: 60 * 60 * 24 * 30 * 6, path: '/' })
    console.info('[WebviewRuntime] WebView app platform set to:', appPlatform)
  },

  registerFinishInterceptor: (interceptor: () => Promise<void> | void) => {
    console.info('[WebviewRuntime] Registering finish interceptor')
    finishInterceptor = interceptor
  },

  unregisterFinishInterceptor: () => {
    console.info('[WebviewRuntime] Unregistering finish interceptor')
    finishInterceptor = null
  }
}
export const webviewRuntime = exposeToWindow(baseRuntime)

/**
 * Redireciona para app://loadUser ou simula o fluxo quando em modo playground.
 * Em modo playground (?playground=true), usa os tokens web existentes para simular
 * a comunicação com o app nativo, evitando a navegação para o URL scheme `app://`.
 * @returns true se a simulação foi aplicada (playground), false se fez o redirect real
 */
export function redirectToLoadUser(): boolean {
  if (typeof window === 'undefined') return false

  if (isPlaygroundMode()) {
    const cookies = isomorphicCookies.getAll()
    if (cookies.token && cookies.refreshToken) {
      webviewRuntime.webviewGetTokens({ token: cookies.token, refreshToken: cookies.refreshToken })
    }
    if (cookies.identityId && cookies.accountId) {
      webviewRuntime.webviewGetUserData({ identityId: cookies.identityId, accountId: cookies.accountId })
    }
    console.info('[WebviewRuntime] Playground: simulated app://loadUser with web tokens')
    notifyPlayground(
      'info',
      'Simulacao de app nativo: carregamento de usuario simulado com tokens web (app://loadUser)'
    )
    return true
  }

  console.info('[WebviewRuntime] Redirecting to app://loadUser')
  webviewLocationReplace(WEBVIEW_LOAD_USER_URL)
  return false
}

declare global {
  interface Window extends WebviewRuntime {
    registerFinishInterceptor: (interceptor: () => Promise<void> | void) => void
    unregisterFinishInterceptor: () => void
  }
}
